import { QueryFunctionContext } from "react-query";
import { JwtClient } from "utils/jwtClient";

import { formatWholesalePriceData } from "./rating.utils";

export type AggregatesQueryContext = {
  endAt: Date;
  expand?: string;
  frequency?: string;
  jwtClient: JwtClient;
  startAt: Date;
  supplyNodeRef: string;
};

export const getAggregates = async ({
  queryKey,
}: QueryFunctionContext<[string, AggregatesQueryContext]>): Promise<
  Aggregates | undefined
> => {
  const [_, { supplyNodeRef, startAt, endAt, frequency, expand, jwtClient }] =
    queryKey;

  let queryParams = {
    supply_node: supplyNodeRef,
    start_at: new Date(startAt).toISOString(),
    end_at: new Date(endAt).toISOString(),
    aggregation_frequency: frequency || "day",
    timezone: "Pacific/Auckland",
  };

  if (expand) {
    queryParams = { ...queryParams, ...{ expand } };
  }

  const response = await jwtClient.apiCall({
    path: "/rating/aggregates",
    queryParams,
  });

  const json = await response.json();

  return json || null;
};

export const getHourlyAggregates = async ({
  queryKey,
}: QueryFunctionContext<[string, AggregatesQueryContext]>): Promise<
  Aggregates | undefined
> => {
  const [key, params] = queryKey;

  return getAggregates({
    queryKey: [
      key,
      {
        ...params,
        expand: "individual_periods",
        frequency: "hour",
      },
    ],
  } as QueryFunctionContext<[string, AggregatesQueryContext]>);
};

/**
 * @description Make call to '/rating/forecast_prices' to get forecasted wholesale prices
 */
export const getForecastedPrices = async ({
  queryKey,
}: QueryFunctionContext<
  [
    string,
    {
      endAt: string | number;
      jwtClient: JwtClient;
      startAt: string | number;
      supplyNodeRef: string;
    },
  ]
>): Promise<ForecastedPrice[]> => {
  const [_, { supplyNodeRef, startAt, endAt, jwtClient }] = queryKey;
  const queryParams = {
    supply_node: supplyNodeRef,
    start_at: new Date(startAt).toISOString(),
    end_at: new Date(endAt).toISOString(),
  };
  const response = await jwtClient.apiCall({
    path: "/rating/forecast_prices",
    queryParams,
  });
  const data = await response.json();
  return data.prices || null;
};

/**
 * @description Make call to '/rating/supply_node_preferences' to get the rating preferences of usage type and location classification
 */
export const getRatingPreferences = async ({
  queryKey,
}: QueryFunctionContext<
  [
    string,
    {
      jwtClient: JwtClient;
      supplyNodeRef: string;
    },
  ]
>): Promise<Array<RatingPreferencesTypes> | undefined> => {
  const [_, { supplyNodeRef, jwtClient }] = queryKey;
  const queryParams = {
    supply_node: supplyNodeRef,
    active_at: new Date().toISOString(),
  };
  const response = await jwtClient.apiCall({
    path: "/rating/supply_node_preferences",
    queryParams,
  });

  const json = await response.json();

  return json.data || null;
};

export const getWholesalePrices = async ({
  queryKey,
}: QueryFunctionContext<
  [
    string,
    {
      jwtClient: JwtClient;
      supplyNodeRef: string;
      startAt: Date;
      endAt: Date;
    },
  ]
>): Promise<Array<WholesalePrice>> => {
  const [_, { supplyNodeRef, startAt, endAt, jwtClient }] = queryKey;
  const queryParams = {
    supply_node: supplyNodeRef,
    start_at: new Date(startAt).toISOString(),
    end_at: new Date(endAt).toISOString(),
  };
  const response = await jwtClient.apiCall({
    path: "/rating/prices",
    queryParams,
  });

  const data = await response.json();

  return data?.prices ? formatWholesalePriceData(data) : null;
};
